import {
  Button,
  ButtonVariants,
  DecisionStatusEnum,
  FormControlSizes,
  FormattedMessage,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  OrdStatusEnum,
  Popover,
  WLCanResubmitCustomerOrder,
  WL_ACTION,
  archiveOrderID,
  includesEnumValue,
  modifyOrder,
  resubmitOrderForm,
  setFocusedOrderID,
  useMixpanel,
  usePopoverState,
  useWLCanCancelCustomerOrder,
  useWLCanModifyCustomerOrder,
  useWLOrderFormDispatch,
  useWLOrderServiceContext,
  useWLRoleAuth,
  type CustomerOrder,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalCancelDialog } from '../../../providers';

const messages = defineMessages({
  modify: {
    defaultMessage: 'Modify',
    id: 'Blotters.ActiveOrdersBlotter.modify',
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: 'Blotters.ActiveOrdersBlotter.cancel',
  },
  archive: {
    defaultMessage: 'Archive',
    id: 'Blotters.ActiveOrdersBlotter.archive',
  },
  pauseOrder: {
    defaultMessage: 'Pause Order',
    id: 'Blotters.ActiveOrdersBlotter.pauseOrder',
  },
  resumeOrder: {
    defaultMessage: 'Resume Order',
    id: 'Blotters.ActiveOrdersBlotter.resumeOrder',
  },
  resubmitOrder: {
    defaultMessage: 'Resubmit Order',
    id: 'Blotters.ActiveOrdersBlotter.resubmitOrder',
  },
});

export const OrderActions = ({ data }: { data: CustomerOrder }) => {
  const { orderService } = useWLOrderServiceContext();
  const dispatch = useWLOrderFormDispatch();
  const mixpanel = useMixpanel();

  const { open: openGlobalCancelDialog } = useGlobalCancelDialog();

  const openOrderStatuses = [
    OrdStatusEnum.New,
    OrdStatusEnum.PendingNew,
    OrdStatusEnum.PendingReplace,
    OrdStatusEnum.Holding,
    OrdStatusEnum.PartiallyFilled,
  ];

  const handleArchiveOrder = useCallback(() => {
    mixpanel.track(MixpanelEvent.ArchiveOrder, {
      [MixpanelEventProperty.ID]: data.OrderID,
    });
    dispatch(archiveOrderID(data.OrderID));
  }, [data, dispatch, mixpanel]);

  const orderFormDispatch = useWLOrderFormDispatch();
  const { isAuthorized } = useWLRoleAuth();
  const canCancelCustomerOrder = useWLCanCancelCustomerOrder();
  const canModifyCustomerOrder = useWLCanModifyCustomerOrder();
  const isOpenOrder = includesEnumValue(openOrderStatuses, data.OrdStatus);
  const isModificationAllowed = canModifyCustomerOrder(data);
  const isCancellationAllowed = canCancelCustomerOrder(data);

  const handleClickModifyOrder = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!isModificationAllowed) {
        return;
      }
      mixpanel.track(MixpanelEvent.OpenModifyOrder, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter,
      });
      e.preventDefault();
      orderFormDispatch(modifyOrder(data));
      // Timeout here to dismiss focused order id
      setTimeout(() => {
        orderFormDispatch(setFocusedOrderID(null));
      });
    },
    [data, isModificationAllowed, orderFormDispatch, mixpanel]
  );

  const handleClickCancelOrder = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!isCancellationAllowed) {
        return;
      }
      e.preventDefault();
      openGlobalCancelDialog(data.OrderID);
      // Timeout here to dismiss focused order id
      setTimeout(() => {
        orderFormDispatch(setFocusedOrderID(null));
      });
    },
    [data.OrderID, isCancellationAllowed, openGlobalCancelDialog, orderFormDispatch]
  );
  const handlePauseOrder = useCallback(() => {
    orderService.pauseOrder(data.OrderID);
    orderFormDispatch(setFocusedOrderID(data.OrderID));
    mixpanel.track(MixpanelEvent.PauseOrder, { [MixpanelEventProperty.Source]: MixpanelEventSource.OrdersBlotter });
  }, [orderService, data.OrderID, mixpanel, orderFormDispatch]);

  const handleResumeOrder = useCallback(() => {
    mixpanel.track(MixpanelEvent.ResumeOrder, {
      [MixpanelEventProperty.ID]: data.OrderID,
    });
    orderService.resumeOrder(data.OrderID);
    orderFormDispatch(setFocusedOrderID(data.OrderID));
  }, [data, orderService, mixpanel, orderFormDispatch]);

  const handleResubmitOrder = useCallback(() => {
    dispatch(resubmitOrderForm(data));
    mixpanel.track(MixpanelEvent.OpenResubmitOrder, {
      [MixpanelEventProperty.ID]: data.OrderID,
    });
  }, [data, dispatch, mixpanel]);

  const popover = usePopoverState({
    trigger: 'click',
    usePortal: true,
    placement: 'bottom-end',
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top-end'],
        },
      },
    ],
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center' }} data-testid="order-actions">
      {isOpenOrder ? (
        <>
          {isAuthorized(WL_ACTION.MODIFY_ORDER) && (
            <Button
              ghost
              onClick={handleClickModifyOrder}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Primary}
              disabled={!isModificationAllowed}
              data-testid="order-actions-modify"
            >
              <FormattedMessage {...messages.modify} />
            </Button>
          )}
          {isAuthorized(WL_ACTION.CANCEL_ORDER) && (
            <Button
              ghost
              onClick={handleClickCancelOrder}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Negative}
              disabled={!isCancellationAllowed}
              data-testid="order-actions-cancel"
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
          )}
        </>
      ) : (
        <Button ghost onClick={handleArchiveOrder} size={FormControlSizes.Small} data-testid="order-actions-archive">
          <FormattedMessage {...messages.archive} />
        </Button>
      )}

      {((isOpenOrder && isAuthorized(WL_ACTION.PAUSE_ORDER)) ||
        (!isOpenOrder && isAuthorized(WL_ACTION.INITIATE_ORDER))) && (
        <Popover {...popover}>
          <IconButton icon={IconName.DotsVertical} ghost data-testid="order-actions-vertical-expand" />
          <div>
            {isOpenOrder ? (
              <>
                <div>
                  <Button
                    disabled={!isOpenOrder || data.DecisionStatus === DecisionStatusEnum.Paused}
                    onClick={handlePauseOrder}
                    size={FormControlSizes.Small}
                    data-testid="order-actions-pause"
                  >
                    <FormattedMessage {...messages.pauseOrder} />
                  </Button>
                </div>
                <div>
                  <Button
                    disabled={!isOpenOrder || data.DecisionStatus !== DecisionStatusEnum.Paused}
                    onClick={handleResumeOrder}
                    size={FormControlSizes.Small}
                    data-testid="order-actions-resume"
                  >
                    <FormattedMessage {...messages.resumeOrder} />
                  </Button>
                </div>
              </>
            ) : (
              <div>
                <Button
                  ghost
                  onClick={handleResubmitOrder}
                  size={FormControlSizes.Small}
                  disabled={!WLCanResubmitCustomerOrder(data)}
                  data-testid="order-actions-resubmit"
                >
                  <FormattedMessage {...messages.resubmitOrder} />
                </Button>
              </div>
            )}
          </div>
        </Popover>
      )}
    </div>
  );
};
